// utils/exportUtils.js
import { getCustomerHistory } from '../hooks/useCustomerHistory';
import { getKoreanTime, formatKoreanDate } from '../utils/timeUtils';
import { idbGetWorkDiaries } from '../utils/idb';

export const exportCustomersToExcel = async (customersByStage, businessType = 'insurance') => {
    try {
        // CSV 포맷으로 생성 후 .xlsx 확장자로 저장 (엑셀에서 자동 인식)
        const csv = await generateCustomersCsv(customersByStage);
        downloadCsvFile(csv, 'fcfunnel_customers');
        alert('엑셀 파일이 다운로드되었습니다. (CSV 형식)');
    } catch (e) {
        console.error('exportCustomersToExcel 실패:', e);
        throw e;
    }
};

export const generateCustomersCsv = async (customers) => {
    const headers = [
        'stage', 'id', 'name', 'phone', 'company', 'birthdate', 'value', 'priority', 'memo', 'history'
    ];
    const stageOrder = [
        'prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'
    ];

    const stageTitleMap = {
        prospect: '가망고객',
        preparation: '연락대상',
        appointment: '약속확정',
        consultation: '상담완료',
        closed: '계약완료',
        rejected: '보류/거절',
        bank: '고객뱅크'
    };

    const rows = [];
    for (const stage of stageOrder) {
        const list = customers[stage] || [];
        for (const item of list) {
            // 히스토리 데이터 가져오기 (비동기)
            let history = [];
            try {
                history = await getCustomerHistory(item.id);
            } catch (_) { /* ignore */ }
            const historyJson = JSON.stringify(history || []);

            const row = [
                stageTitleMap[stage] || stage,
                item.id ?? '',
                item.name ?? '',
                item.phone ?? '',
                item.company ?? '',
                item.birthdate ?? '',
                item.value ?? '',
                item.priority ?? '',
                (item.memo || '').toString().replace(/\r?\n/g, ' '),
                historyJson
            ];
            rows.push(row);
        }
    }

    const escapeCell = (value) => {
        const str = String(value).replace(/\r?\n/g, ' ');
        const needsQuote = /[",\n]/.test(str);
        const escaped = str.replace(/"/g, '""');
        return needsQuote ? `"${escaped}"` : escaped;
    };

    const csv = [headers.join(','), ...rows.map(r => r.map(escapeCell).join(','))].join('\n');
    return csv;
};

export const downloadCsvFile = (csv, filenamePrefix = 'fcfunnel_backup') => {
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const koreanTime = getKoreanTime();
    const timestamp = koreanTime.toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const filename = `${filenamePrefix}_${timestamp}.csv`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// 히스토리 복원을 위한 함수
export const restoreCustomerHistory = (customerId, historyJson) => {
    if (!customerId || !historyJson) return;

    try {
        const history = JSON.parse(historyJson);
        if (Array.isArray(history)) {
            const HISTORY_STORAGE_KEY = 'customerHistory';
            const store = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || '{}');
            store[String(customerId)] = history;
            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(store));
        }
    } catch (error) {
        console.warn('히스토리 복원 실패:', error);
    }
};

// 한 CSV 파일에 "고객" 표와 "히스토리" 표를 섹션으로 연속 배치하여 내보내기
export const exportCustomersCsvWithSections = async (customersByStage) => {
    // 고객 섹션 데이터 구성
    const customerHeaders = ['stage', 'id', 'name', 'phone', 'company', 'birthdate', 'value', 'priority', 'memo'];
    const stageOrder = ['prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'];
    const stageTitleMap = {
        prospect: '가망고객',
        preparation: '연락대상',
        appointment: '약속확정',
        consultation: '상담완료',
        closed: '계약완료',
        rejected: '보류/거절',
        bank: '고객뱅크'
    };

    const customerRows = [customerHeaders];
    const allCustomersFlat = [];
    for (const stage of stageOrder) {
        const list = customersByStage[stage] || [];
        for (const item of list) {
            allCustomersFlat.push(item);
            customerRows.push([
                stageTitleMap[stage] || stage,
                item.id ?? '',
                item.name ?? '',
                item.phone ?? '',
                item.company ?? '',
                item.birthdate ?? '',
                item.value ?? '',
                item.priority ?? '',
                (item.memo || '').toString().replace(/\r?\n/g, ' ')
            ]);
        }
    }

    // 히스토리 섹션 데이터 구성
    const historyHeaders = ['customerId', 'name', 'phone', 'type', 'note', 'at', 'followUpDate', 'followUpNote'];
    const historyRows = [historyHeaders];
    for (const c of allCustomersFlat) {
        let history = [];
        try {
            history = await getCustomerHistory(c.id);
        } catch (_) { }
        if (Array.isArray(history)) {
            for (const h of history) {
                let followUpDate = '';
                let followUpNote = '';
                if (typeof h?.followUp === 'object' && h?.followUp) {
                    if (h.followUp.date) followUpDate = h.followUp.date;
                    if (h.followUp.note) followUpNote = String(h.followUp.note).replace(/\r?\n/g, ' ');
                    if (!followUpNote && h.followUp.content) followUpNote = String(h.followUp.content).replace(/\r?\n/g, ' ');
                } else if (typeof h?.followUp === 'string') {
                    followUpDate = h.followUp;
                }
                historyRows.push([
                    c.id ?? '',
                    c.name ?? '',
                    c.phone ?? '',
                    h?.type ?? '',
                    (h?.note || '').toString().replace(/\r?\n/g, ' '),
                    h?.at ? formatKoreanDate(h.at) : '',
                    followUpDate,
                    followUpNote
                ]);
            }
        }
    }

    // CSV 직렬화 유틸
    const escapeCell = (value) => {
        const str = String(value).replace(/\r?\n/g, ' ');
        const needsQuote = /[",\n]/.test(str);
        const escaped = str.replace(/"/g, '""');
        return needsQuote ? `"${escaped}"` : escaped;
    };
    const toCsv = (rows) => [rows[0].join(','), ...rows.slice(1).map(r => r.map(escapeCell).join(','))].join('\n');

    // 업무일지 섹션 데이터 구성
    const workDiaryHeaders = ['date', 'title', 'content', 'createdAt', 'updatedAt'];
    const workDiaryRows = [workDiaryHeaders];
    try {
        const workDiaries = await idbGetWorkDiaries();
        if (Array.isArray(workDiaries)) {
            // 날짜순으로 정렬 (오래된 것부터)
            const sortedDiaries = [...workDiaries].sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';
                return dateA.localeCompare(dateB);
            });
            for (const diary of sortedDiaries) {
                workDiaryRows.push([
                    diary.date ?? '',
                    (diary.title || '').toString().replace(/\r?\n/g, ' '),
                    (diary.content || '').toString().replace(/\r?\n/g, ' '),
                    diary.createdAt ? formatKoreanDate(diary.createdAt) : '',
                    diary.updatedAt ? formatKoreanDate(diary.updatedAt) : ''
                ]);
            }
        }
    } catch (error) {
        console.warn('업무일지 데이터 가져오기 실패:', error);
    }

    // 섹션 라벨과 공백 라인 포함하여 하나의 CSV 문자열로 결합
    const parts = [];
    parts.push('# 고객');
    parts.push(toCsv(customerRows));
    parts.push(''); // 빈 줄로 구분
    parts.push('# 히스토리');
    parts.push(toCsv(historyRows));
    parts.push(''); // 빈 줄로 구분
    parts.push('# 업무일지');
    parts.push(toCsv(workDiaryRows));
    const finalCsv = parts.join('\n');

    downloadCsvFile(finalCsv, 'fcfunnel_customers_with_history');
};

